import { Loading } from '@/core/ui/components/Loading';

import React, { useState, useEffect } from 'react';
import { useServices, useToast, getErrorMessage } from '@/app/providers';
import { useI18n } from '@/shared/i18n';
import { Ticket, TicketStatus, User } from '@/shared/types';
import { TicketList } from '@/presentation/components/organisms/TicketList';
import { TicketDetail } from '@/presentation/components/organisms/TicketDetail';
import { isSectionAdmin, useAbility } from '@/modules/identity/application/ability';

export const Incidents = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { ticketRepo, userRepo } = useServices();
  const ability = useAbility();
  const canCreateTicket = ability.can('manage', 'all') || ability.can('create', 'Ticket');
  const canUpdateTicket = ability.can('manage', 'all') || ability.can('update', 'Ticket');
  
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setLoading(true);
    try {
      const user = await userRepo.getCurrentUser();
      setCurrentUser(user);
      
      let data: Ticket[];
      if (user.role === 'ADMIN' || user.role === 'GUARD' || isSectionAdmin(user)) {
        data = await ticketRepo.getAllTickets();
      } else {
        data = await ticketRepo.getUserTickets(user.id);
      }
      setTickets(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (ticketData: Partial<Ticket>) => {
    if (!currentUser) return;
    if (!canCreateTicket) return;
    try {
      await ticketRepo.createTicket({
        ...ticketData,
        userId: currentUser.id,
        title: ticketData.title || 'Reporte',
        description: ticketData.description || '',
        category: ticketData.category || 'OTHER',
      } as any);
      await init(); // Refresh list
    } catch (e) {
      toast.error(getErrorMessage(e), 'Error al crear reporte');
    }
  };

  const handleStatusChange = async (status: TicketStatus) => {
    if (!selectedTicket) return;
    if (!canUpdateTicket) return;
    try {
      await ticketRepo.updateTicketStatus(selectedTicket.id, status);
      setSelectedTicket({ ...selectedTicket, status });
      await init(); // Refresh background list
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleAddComment = async (text: string) => {
    if (!selectedTicket || !currentUser) return;
    if (!canUpdateTicket) return;
    try {
      const newComment = await ticketRepo.addComment(selectedTicket.id, text, currentUser);
      setSelectedTicket({ 
        ...selectedTicket, 
        comments: [...selectedTicket.comments, newComment] 
      });
      await init();
    } catch (e) {
      console.error(e);
    }
  };

  if (loading || !currentUser) return <Loading />;

  return (
    <div className="max-w-4xl mx-auto h-full">
      {selectedTicket ? (
        <TicketDetail 
          ticket={selectedTicket} 
          currentUser={currentUser}
          onBack={() => setSelectedTicket(null)}
          onAddComment={handleAddComment}
          onStatusChange={handleStatusChange}
          canUpdate={canUpdateTicket}
        />
      ) : (
        <TicketList 
          tickets={tickets} 
          onCreate={handleCreate}
          onSelect={setSelectedTicket}
          canCreate={canCreateTicket}
        />
      )}
    </div>
  );
};
